import { Component, OnInit } from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  FormControl,
  ValidationErrors,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NotificationsService } from "../../../../theme/services/notifications/notifications.service";
import { Router, ActivatedRoute } from "@angular/router";
import { CandidateDisabilitiesService } from "../../services/candidate-disabilities.service";
import {
  documentsTypeDisabilities,
  TypesOfDisabilitiesAdmin,
  ListInsurances
} from "../candidate-disabilities/data.constants";
import { Message } from "primeng/primeng";
import { getDiffDateDisabilty } from "../../../../theme/functions/getDiffDate.function";

@Component({
  selector: "app-disabily-correct",
  templateUrl: "./disabily-correct.component.html",
  styleUrls: ["./disabily-correct.component.scss"],
})
export class DisabilyCorrectComponent implements OnInit {
  public form: FormGroup;
  public documentsTypeDisabilities = documentsTypeDisabilities;
  public edit: any;
  public selectedDisability: string | null = null;
  private id: number;
  private exp: string = '';
  private sig: string = '';
  public msgs: Message[] = [];
  public promiseForm: Promise<any>;
  public formGroupNameDocuments = "documents";
  public daysDifference: number = 0;
  public isProrroga: boolean = false;
  public hiddenTypeDocumentIds: number[] = [];
  public dialogVisibilityKey: string | null = null;

  constructor(
    private fb: FormBuilder,
    public translate: TranslateService,
    private disabilityService: CandidateDisabilitiesService,
    private notificationsService: NotificationsService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    let currentLang = "es";
    this.translate.use(currentLang);
  }

  ngOnInit(): void {
    // Inicializamos el formulario con los controles básicos
    this.form = this.fb.group({});

    // Obtenemos la 'id' de la URL y los parámetros de firma
    this.id = +this.route.snapshot.paramMap.get("id");
    this.exp = this.route.snapshot.queryParamMap.get("exp") || '';
    this.sig = this.route.snapshot.queryParamMap.get("sig") || '';

    if (!this.exp || !this.sig) {
      this.notificationsService.growlNotify("error", "Enlace vencido o inválido. Solicite uno nuevo.");
      this.router.navigateByUrl("/login-candidate");
      return;
    }

    if (this.id !== 0 && this.id !== undefined) {
      // Llamamos al servicio con la id y los parámetros de firma
      this.disabilityService
        .getDocumentsDisabilityPublic(this.id, this.exp, this.sig)
        .toPromise()
        .then(
          (response) => {
            this.edit = response.json();
            this.dialogVisibilityKey = this.getInsuranceCode(
              this.edit.insurance_id,
            );

            // Calcular la diferencia de días entre start_date y end_date
            if (this.edit.start_date && this.edit.end_date) {
              this.daysDifference = getDiffDateDisabilty(
                this.edit.start_date,
                this.edit.end_date,
              );
            }

            // Verificar si es prórroga (type_of_contingency_id === 2)
            this.isProrroga = this.edit.type_of_contingency_id === 2;

            // Asignamos el valor de list_disabilities_type_id a selectedDisability
            const disabilityType = TypesOfDisabilitiesAdmin.find(
              (type) => type.value == this.edit.list_disabilities_type_id,
            );

            if (disabilityType) {
              this.selectedDisability = disabilityType.value_form;
            }

            // Determinar qué documentos ocultar según las reglas de negocio
            this.determineHiddenTypeDocuments();

            this.createNewRequestForm();
          },
          (error) => {
            this.notificationsService.growlNotify(
              "error",
              `ID ${this.id} no encontrado`,
            );
            this.router.navigateByUrl("/login-candidate");
          },
        );
    }
  }

  private getInsuranceCode(insuranceId?: string): string | null {
    if (!insuranceId) {
      return null;
    }

    const normalized = insuranceId.trim();
    const match = ListInsurances.find(
      (item) =>
        item.value.trim().toLowerCase() === normalized.toLowerCase() ||
        item.label.trim().toLowerCase() === normalized.toLowerCase(),
    );

    return match ? match.value : normalized;
  }

  // método para determinar qué documentos ocultar
  private determineHiddenTypeDocuments(): void {
    this.hiddenTypeDocumentIds = [];

    // Si es prórroga, mostrar todos los tipos de documentos
    if (this.isProrroga) {
      return;
    }

    // Si NO es prórroga y la diferencia es < 3 días, ocultar epicrisis
    if (this.daysDifference < 3) {
      const epicrisisIds = [2, 6]; // Summary Of Care / Epicrisis y Epicrisis With Weeks Of Gestation
      this.hiddenTypeDocumentIds = epicrisisIds;
    }
    // Si es >= 3 días, mostrar todos (no ocultar nada)
  }

  public cancel() {
    this.router.navigateByUrl("/login-candidate");
    this.form.reset();
  }

  public createNewRequestForm() {
    this.form = this.fb.group({});
    const selectedDisability = this.documentsTypeDisabilities.find(
      (tipo) => tipo.key == this.selectedDisability,
    );
    const formGroupKey = this.formGroupNameDocuments;
    if (selectedDisability) {
      const group = this.fb.group({});

      // Agregar el campo 'edit' en 'yes' porque siempre está en modo edición
      group.addControl("edit", this.fb.control("yes")); // Modo edición siempre

      // Agregar los FormArray como en el código original
      selectedDisability.typedocuments.forEach((doc) => {
        const formArrayName = doc.formNameArray;
        const formArray = this.fb.array([]);

        // Filtramos los documentos del backend que corresponden al tipo de documento actual
        const matchingDocuments = this.edit.documents.filter(
          (existingDoc) =>
            existingDoc.list_documents_type_disabilities_id == doc.id,
        );

        if (matchingDocuments.length > 0) {
          // Si hay documentos que coinciden con el tipo de documento, los agregamos al FormArray
          matchingDocuments.forEach((existingDoc) => {
            // Como estamos en modo edición, agregamos la propiedad 'edit' al documento
            existingDoc.edit = "yes"; // No se puede eliminar
            formArray.push(this.fb.control(existingDoc)); // Usamos el documento existente
          });
        }

        // Determinar si este tipo de documento requiere un nuevo documento
        const requiresNewDocument = this.shouldRequireNewDocument(doc.id);

        // Configurar validación
        if (requiresNewDocument) {
          // Si es prórroga o es epicrisis con >= 3 días, requiere al menos un documento nuevo
          formArray.setValidators([
            (control: FormArray) => {
              // Verificamos si hay al menos un documento nuevo (sin id ni edit)
              const hasNewDocument = control.controls.some(
                (ctrl) => !ctrl.value.id && !ctrl.value.edit,
              );
              return hasNewDocument ? null : { required: true };
            },
          ]);
        } else {
          // Para otros casos, mantener la validación original
          // Siempre se requiere al menos un documento nuevo en todo el formulario
          formArray.setValidators([
            (control: FormArray) => {
              // No requiere validación específica aquí, la validación general se hace en otro lado
              return null;
            },
          ]);
        }

        // Agregamos el FormArray al grupo
        group.addControl(formArrayName, formArray);
      });

      // Finalmente, agregamos el grupo al FormGroup principal
      this.form.addControl(formGroupKey, group);
    }
  }

  // Método para determinar si un tipo de documento específico requiere un nuevo documento
  private shouldRequireNewDocument(documentTypeId: number): boolean {
    // Si es prórroga, TODOS los tipos de documentos requieren al menos uno nuevo
    if (this.isProrroga) {
      return true;
    }

    // Si NO es prórroga:
    // - Si la diferencia es < 3 días: epicrisis NO es obligatorio, los demás SÍ
    // - Si la diferencia es >= 3 días: TODOS son obligatorios incluyendo epicrisis
    const epicrisisIds = [2, 6]; // Summary Of Care / Epicrisis y Epicrisis With Weeks Of Gestation

    if (this.daysDifference < 3) {
      // Menos de 3 días: epicrisis NO es obligatorio
      return !epicrisisIds.includes(documentTypeId);
    } else {
      // 3 días o más: TODOS son obligatorios
      return true;
    }
  }

  public saveAndExit() {
    this.createFormDataAndSave();
    this.router.navigateByUrl("/incapacidades");
  }

  public createFormDataAndSave() {
    // Creación del FormData como lo tienes actualmente
    const formData = new FormData();
    const documentsGroup = this.form.get(
      this.formGroupNameDocuments,
    ) as FormGroup;

    if (documentsGroup) {
      Object.keys(documentsGroup.controls).forEach((key) => {
        const control = documentsGroup.get(key);

        if (control instanceof FormArray) {
          const formArray = control as FormArray;

          formArray.controls.forEach((file: FormControl) => {
            if (file.value && !file.value.id && !file.value.edit) {
              formData.append(`documents[${key}][]`, file.value);
            }
          });
        }
      });
    }

    formData.append("idDisability", this.edit.id);

    // Hacemos la llamada a la promesa
    this.promiseForm = this.disabilityService
      .uploadCorrectDocumentsDisabilityPublic(formData, this.exp, this.sig)
      .toPromise()
      .then((response) => {
        this.msgs = [];
        const { message } = JSON.parse(response["_body"]);
        this.msgs.push({ severity: "success", summary: message });
        // Mostrar alerta cuando la incapacidad se suba correctamente
        alert("¡La incapacidad ha sido subida correctamente!");
        this.resetForm();
      })
      .catch((error) => {
        this.msgs = [];
        const { message } = JSON.parse(error["_body"]);
        this.msgs.push({ severity: "error", summary: message });
        alert("¡Error Al Subir La Incapacidad, Intentenlo De Nuevo!");
      });
  }

  public resetForm() {
    // Obtener el FormGroup correspondiente a la discapacidad seleccionada
    const documentsGroup = this.form.get(
      this.formGroupNameDocuments,
    ) as FormGroup;

    // Verifica que el FormGroup existe antes de continuar
    if (documentsGroup) {
      // Recorremos todos los FormArrays dentro del FormGroup
      Object.keys(documentsGroup.controls).forEach((controlName) => {
        const control = documentsGroup.get(controlName);

        // Si el control es un FormArray, lo vaciamos
        if (control instanceof FormArray) {
          while (control.length) {
            control.removeAt(0); // Elimina todos los controles dentro del FormArray
          }
        }
      });
    }
  }

  public getFormValidationErrors(form: FormGroup): string[] {
    let errors: string[] = [];
    const array_forms = Object.keys(form.controls);

    // Obtener los errores de los controles
    errors = this.getErrorString(array_forms, form);

    // Devolver el primer error que encuentre
    return errors.length ? [errors[0]] : [];
  }

  public getErrorString(array_forms: any, form_local: any): string[] {
    let errors: string[] = [];

    for (let i = 0; i < array_forms.length; i++) {
      const key = array_forms[i];
      const control = form_local.get(key);

      // Si el control es un FormGroup, iteramos sobre sus controles internos
      if (control instanceof FormGroup) {
        // Llamada recursiva a los controles internos del FormGroup
        errors = errors.concat(
          this.getErrorString(Object.keys(control.controls), control),
        );
      }
      // Si el control tiene errores, lo procesamos
      else if (control.errors) {
        const controlErrors: ValidationErrors = control.errors;
        const keyError = Object.keys(controlErrors)[0];
        let key_with_spaces = this.deleteUnderLine(key);
        let key_translated = "";
        let error_translated = "";

        // Traducción del nombre del campo
        this.translate.get(key_with_spaces).subscribe((res: string) => {
          key_translated = res;
        });

        // Traducción del error
        this.translate.get(keyError).subscribe((res: string) => {
          error_translated = res;
        });

        // Almacenamos el primer error en el arreglo de errores
        if (key_translated && error_translated) {
          errors.push(`Campo: ${key_translated}, Error: ${error_translated}`);
        }
      }
    }

    return errors;
  }

  public deleteUnderLine(chain: string): string {
    return chain.replace(/_/g, " ");
  }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/disability/components/disabily-correct/disabily-correct.component.ts